import * as React from 'react'
import { ChevronsUpDown } from 'lucide-react'

import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { TAX_TYPE_OPTIONS } from '@/lib/tax-types'

interface TaxTypeComboboxProps {
  value: string
  onChange: (next: string) => void
  onBlur?: () => void
  disabled?: boolean
  id?: string
}

export function TaxTypeCombobox({
  value,
  onChange,
  onBlur,
  disabled,
  id,
}: TaxTypeComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const inputRef = React.useRef<HTMLInputElement>(null)

  const filtered = React.useMemo(() => {
    const trimmed = value.trim().toLowerCase()
    if (!trimmed) return TAX_TYPE_OPTIONS
    return TAX_TYPE_OPTIONS.filter((opt) =>
      opt.toLowerCase().includes(trimmed),
    )
  }, [value])

  const handleSelect = (next: string) => {
    onChange(next)
    setOpen(false)
    inputRef.current?.focus()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className="relative">
          <Input
            ref={inputRef}
            id={id}
            type="text"
            role="combobox"
            aria-expanded={open}
            aria-autocomplete="list"
            autoComplete="off"
            value={value}
            onChange={(e) => {
              onChange(e.target.value)
              if (!open) setOpen(true)
            }}
            onFocus={() => setOpen(true)}
            onBlur={onBlur}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                setOpen(false)
              } else if (e.key === 'ArrowDown' && !open) {
                setOpen(true)
              }
            }}
            placeholder="e.g., County Sales"
            disabled={disabled}
            className="pr-9"
          />
          <button
            type="button"
            aria-label="Toggle suggestions"
            tabIndex={-1}
            disabled={disabled}
            onClick={() => {
              setOpen((o) => !o)
              inputRef.current?.focus()
            }}
            className="absolute inset-y-0 right-0 flex w-9 items-center justify-center text-muted-foreground hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
          >
            <ChevronsUpDown className="h-4 w-4" />
          </button>
        </div>
      </PopoverAnchor>
      <PopoverContent
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
        onInteractOutside={(e) => {
          if (e.target instanceof Element && inputRef.current?.contains(e.target)) {
            e.preventDefault()
          }
        }}
        className="w-[var(--radix-popover-trigger-width)] p-1"
      >
        <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          Suggestions
        </div>
        {filtered.length === 0 ? (
          <div className="px-2 py-1.5 text-sm text-muted-foreground">
            No matching suggestions
          </div>
        ) : (
          <div className="grid">
            {filtered.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => handleSelect(opt)}
                className={cn(
                  'flex items-center rounded-sm px-2 py-1.5 text-left text-sm outline-none',
                  'hover:bg-accent hover:text-accent-foreground focus-visible:bg-accent focus-visible:text-accent-foreground',
                  opt === value && 'bg-accent/50',
                )}
              >
                {opt}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
